/**
 * Audiences on the Access screen (ADR-034 phase 2). An app may place each of
 * its records in an audience — a readership it declares: who reads those
 * records (any of its reader permissions) and who may file records there.
 * Fias enforces it on every request; these cards show it, and let an owner
 * do the two things only a person may:
 *
 *   - finish turning roles on: records the workspace wrote before roles were
 *     on are in no audience, and an owner chooses where each collection's go
 *     (also the retry when placing them failed);
 *   - re-disclose: an audience the app retired reads to nobody, so its
 *     records wait until an owner shows them to a live audience — a widening,
 *     so it needs the owner's step-up and the other owners are told.
 *
 * Labels are the app's own words, shown attributed to it. Counts only, never
 * content.
 */
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import type {
  AppAccessActivationSetupDto,
  AppAccessAudienceDto,
  AppAccessCollectionRecordsDto,
  AppAccessScreenDto,
} from '@/lib/api';
import { StepUpDialog } from '../../components/StepUpDialog';
import {
  ACCESS_BUSY_RETRY_MS,
  useCompleteAppRolesActivation,
  useRedisclose,
  type AppAccessScope,
} from '../../hooks/useAppAccess';
import { AppText } from './AttributedText';
import {
  appAccessErrorCode,
  audienceLabel,
  describeAppAccessError,
  permissionLabel,
} from './shared';

/** The live audiences a collection lists — where its records may be placed. */
export function placeableAudiences(
  audiences: readonly AppAccessAudienceDto[],
  records: AppAccessCollectionRecordsDto,
): AppAccessAudienceDto[] {
  return audiences.filter((a) => !a.deprecated && records.audiences.includes(a.id));
}

/** A chooser of where one collection's records go. */
export function PlacementChooser({
  records,
  audiences,
  value,
  onChange,
  disabled,
  appName,
}: {
  records: AppAccessCollectionRecordsDto;
  audiences: readonly AppAccessAudienceDto[];
  value: string | undefined;
  onChange: (audience: string) => void;
  disabled?: boolean;
  appName: string;
}) {
  const id = `place-${records.collection}`;
  return (
    <div className="grid gap-1.5 sm:grid-cols-[1fr_16rem] sm:items-center">
      <Label htmlFor={id} className="font-normal">
        <span className="font-mono text-xs">{records.collection}</span> —{' '}
        {records.unplaced === 1 ? '1 record' : `${records.unplaced} records`}
      </Label>
      <select
        id={id}
        className="h-9 rounded-md border border-input bg-background px-2"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Choose who reads them…</option>
        {placeableAudiences(audiences, records).map((a) => (
          <option key={a.id} value={a.id}>
            {a.label}
            {a.sensitive ? ' (sensitive)' : ''} — from {appName}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The app's audiences: who reads each, and who files records in it. */
export function AudiencesCard({
  appName,
  access,
  records,
}: {
  appName: string;
  access: AppAccessScreenDto['access'];
  records: readonly AppAccessCollectionRecordsDto[];
}) {
  if (access.audiences.length === 0) return null;
  const counts = new Map<string, number>();
  for (const r of records) {
    for (const [audience, n] of Object.entries(r.byAudience)) {
      counts.set(audience, (counts.get(audience) ?? 0) + n);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Audiences</CardTitle>
        <CardDescription>
          {appName} places each of its records here in an audience. Only people whose roles read an
          audience see its records — Fias checks it on every request, whatever the app shows. An
          audience never gains readers: the app would declare a new one instead.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {access.audiences.map((a) => (
          <div key={a.id} className="rounded-md border border-border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">
                <AppText appName={appName}>{a.label}</AppText>
              </span>
              {a.sensitive && <Badge variant="destructive">Sensitive</Badge>}
              {a.deprecated && <Badge variant="secondary">Retired</Badge>}
              <span className="text-xs text-muted-foreground">
                {(counts.get(a.id) ?? 0) === 1 ? '1 record' : `${counts.get(a.id) ?? 0} records`}
              </span>
            </div>
            {a.description && (
              <p className="mt-1 text-muted-foreground">
                <AppText appName={appName}>{a.description}</AppText>
              </p>
            )}
            {a.deprecated ? (
              <p className="mt-1 text-muted-foreground">
                Retired by the app: its records read to nobody until an owner shows them to another
                audience.
              </p>
            ) : (
              <p className="mt-1 text-muted-foreground">
                Read with{' '}
                {a.readers.map((p) => `“${permissionLabel(access.permissions, p)}”`).join(' or ')};{' '}
                {/* Only live permissions are listed: a placer missing is retired. */}
                {access.permissions.some((p) => p.id === a.disclosedBy)
                  ? `filed into with “${permissionLabel(access.permissions, a.disclosedBy)}”.`
                  : 'nobody can file new records into it any more.'}
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Collections holding records in no audience that have a live audience to go to. */
export function recordsNeedingPlace(
  audiences: readonly AppAccessAudienceDto[],
  records: readonly AppAccessCollectionRecordsDto[],
): AppAccessCollectionRecordsDto[] {
  return records.filter((r) => r.unplaced > 0 && placeableAudiences(audiences, r).length > 0);
}

/**
 * Most requests one "Place records and finish" makes. Each places for up to
 * ~20 s and answers PLACEMENT_IN_PROGRESS while records remain, so this is
 * about ten minutes of placing before the owner is asked to carry on.
 */
export const MAX_PLACEMENT_ROUNDS = 30;

/**
 * How many times in a row placing goes on by itself after `PLACEMENT_BUSY`
 * (a batch met a lock or time limit; nothing of it was kept) before it says so.
 */
export const MAX_PLACEMENT_BUSY_RETRIES = 2;

/**
 * Turning roles on is not finished (`activating`): the app's collections with
 * audiences stay closed until the records written before are placed. An owner
 * chooses where each collection's go (also the retry when placing failed). A
 * collection whose audiences the app has all retired has nowhere to go: its
 * records stay unreadable, and never hold setup up. Once roles are on,
 * records still in no audience are placed from {@link RedisclosureCard}.
 *
 * Placing many records takes several requests (each stops after a time
 * budget with PLACEMENT_IN_PROGRESS; what moved stays moved). The card goes
 * on by itself — up to MAX_PLACEMENT_ROUNDS, showing what is placed so far,
 * stopping at the first other answer and showing it. It also carries on, once
 * per visit, when it opens on records still waiting whose places are all
 * chosen (an activation that answered "still placing", or a visit after an
 * interrupted one): the owner chose those places already.
 */
export function FinishSetupCard({
  scope,
  appName,
  access,
  records,
  isOwner,
}: {
  scope: AppAccessScope;
  appName: string;
  access: AppAccessScreenDto['access'];
  records: readonly AppAccessCollectionRecordsDto[];
  isOwner: boolean;
}) {
  const complete = useCompleteAppRolesActivation(scope);
  const activating = access.activation?.mode === 'activating';
  const waiting = recordsNeedingPlace(access.audiences, records);
  const nowhere = records.filter(
    (r) => r.unplaced > 0 && placeableAudiences(access.audiences, r).length === 0,
  );
  const recorded = access.activation?.placements ?? {};
  // Only what this person changes is sent, so another owner's later choices
  // are never overwritten with a stale copy.
  const [changes, setChanges] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<AppAccessActivationSetupDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  // While placing runs: how many records this run placed so far.
  const [placing, setPlacing] = useState<{ placed: number } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const live = new Set(access.audiences.filter((a) => !a.deprecated).map((a) => a.id));
  const choiceFor = (collection: string) => {
    const chosen = changes[collection] ?? recorded[collection];
    return chosen && live.has(chosen) ? chosen : undefined;
  };
  const missing = waiting.filter((r) => !choiceFor(r.collection));

  /** One request: where placing stands after it, or what stopped it. */
  async function placeOnce(
    placements: Record<string, string>,
  ): Promise<{ setup: AppAccessActivationSetupDto } | { failure: unknown }> {
    try {
      return { setup: (await complete.mutateAsync(placements)).setup };
    } catch (err) {
      // A 409 answers where it stands; its `error` (if any) says why.
      const body = (err as { body?: { setup?: AppAccessActivationSetupDto } }).body;
      return body?.setup ? { setup: body.setup } : { failure: err };
    }
  }

  async function finish() {
    if (placing) return;
    setError(null);
    setPlacing({ placed: 0 });
    let placed = 0;
    // The changed choices go with the first request; they are recorded then.
    let placements = changes;
    // Another change to the app's access held its lock: one more try, then say so.
    let busyRetried = false;
    // A batch held up by a lock or time limit (PLACEMENT_BUSY): a couple more.
    let placementBusy = 0;
    try {
      for (let round = 1; round <= MAX_PLACEMENT_ROUNDS; round++) {
        const answer = await placeOnce(placements);
        if (!mounted.current) return;
        if ('failure' in answer) {
          setError(describeAppAccessError(answer.failure));
          return;
        }
        // Any answer of where placing stands means the choices are recorded.
        if (round === 1) setChanges({});
        placements = {};
        const { setup } = answer;
        placed += setup.placed.reduce((n, p) => n + p.count, 0);
        setOutcome(setup);
        setPlacing({ placed });
        const code = setup.error?.code;
        // Placed for this request's time budget, with more to go: carry on.
        if (code === 'PLACEMENT_IN_PROGRESS' && round < MAX_PLACEMENT_ROUNDS) {
          busyRetried = false;
          placementBusy = 0;
          continue;
        }
        if (
          code === 'PLACEMENT_BUSY' &&
          placementBusy < MAX_PLACEMENT_BUSY_RETRIES &&
          round < MAX_PLACEMENT_ROUNDS
        ) {
          placementBusy += 1;
          await new Promise((resolve) => setTimeout(resolve, ACCESS_BUSY_RETRY_MS));
          if (!mounted.current) return;
          continue;
        }
        if (code === 'ACCESS_BUSY' && !busyRetried && round < MAX_PLACEMENT_ROUNDS) {
          busyRetried = true;
          await new Promise((resolve) => setTimeout(resolve, ACCESS_BUSY_RETRY_MS));
          if (!mounted.current) return;
          continue;
        }
        if (setup.error) {
          setError(
            describeAppAccessError({ code: setup.error.code, message: setup.error.message }),
          );
        } else if (setup.mode === 'roles') {
          toast({ title: `${appName}’s roles are on` });
        }
        return;
      }
    } finally {
      if (mounted.current) setPlacing(null);
    }
  }

  // Carry on by itself, once per visit, when records wait and every one of
  // them has a place the owners already chose — recorded with the
  // activation, never one being picked on this card (that waits for a click).
  const autoStarted = useRef(false);
  const canContinue =
    activating &&
    isOwner &&
    waiting.length > 0 &&
    Object.keys(changes).length === 0 &&
    waiting.every((r) => live.has(recorded[r.collection] ?? ''));
  useEffect(() => {
    if (!canContinue || autoStarted.current) return;
    autoStarted.current = true;
    void finish();
    // Keyed on `canContinue` alone: `finish` reads this render's state, and
    // the ref makes it run once per mount.
  }, [canContinue]);

  if (!activating) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Finish turning on roles</CardTitle>
        <CardDescription>
          {`Roles are on, but ${appName}’s records from before need a place: until each kind is placed in an audience, its data with audiences stays closed to everyone.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!isOwner ? (
          <p className="text-muted-foreground">
            An owner of this workspace chooses where these records go.
          </p>
        ) : (
          <>
            {waiting.length === 0 ? (
              <p className="text-muted-foreground">
                Every record has a place; finish to open them.
              </p>
            ) : (
              waiting.map((r) => (
                <PlacementChooser
                  key={r.collection}
                  records={r}
                  audiences={access.audiences}
                  appName={appName}
                  value={choiceFor(r.collection)}
                  disabled={!!placing}
                  onChange={(audience) =>
                    setChanges((prev) => ({ ...prev, [r.collection]: audience }))
                  }
                />
              ))
            )}
            {nowhere.length > 0 && (
              <p className="text-muted-foreground">
                {nowhere.map((r) => r.collection).join(', ')}: {appName} retired every audience
                these could go to, so they stay unreadable.
              </p>
            )}
            {outcome && outcome.placementRequired.length > 0 && (
              <p className="text-destructive">
                Choose a place for {outcome.placementRequired.join(', ')} — records arrived there,
                or the audience chosen was retired, meanwhile.
              </p>
            )}
            {placing && (
              <p className="text-muted-foreground" role="status">
                Placing records…{' '}
                {placing.placed === 1 ? '1 placed so far' : `${placing.placed} placed so far`}. What
                is placed stays placed.
              </p>
            )}
            {error && <p className="text-destructive">{error}</p>}
            <Button onClick={() => void finish()} disabled={missing.length > 0 || !!placing}>
              {placing ? 'Placing records…' : 'Place records and finish'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Records nobody can read, once roles are on: those in an audience the app
 * retired, and those in no audience (written under tier rules in a
 * collection that had nowhere to place them). An owner may show them to live audiences —
 * several collections at once, so records that point at each other move
 * together. A widening, so it is confirmed with step-up and the other owners
 * are emailed.
 */
export function RedisclosureCard({
  scope,
  appName,
  access,
  records,
  isOwner,
}: {
  scope: AppAccessScope;
  appName: string;
  access: AppAccessScreenDto['access'];
  records: readonly AppAccessCollectionRecordsDto[];
  isOwner: boolean;
}) {
  const redisclose = useRedisclose(scope);
  const retired = new Set(access.audiences.filter((a) => a.deprecated).map((a) => a.id));
  const roles = access.activation?.mode === 'roles';
  const waiting: { records: AppAccessCollectionRecordsDto; from: string | null; count: number }[] =
    roles
      ? records.flatMap((r) => [
          ...(r.unplaced > 0 && placeableAudiences(access.audiences, r).length > 0
            ? [{ records: r, from: null, count: r.unplaced }]
            : []),
          ...Object.entries(r.byAudience)
            .filter(([audience, n]) => retired.has(audience) && n > 0)
            .map(([audience, count]) => ({ records: r, from: audience, count })),
        ])
      : [];
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  if (waiting.length === 0) return null;
  const rowKey = (collection: string, from: string | null) => `${collection}:${from ?? ''}`;
  const chosen = waiting
    .filter((w) => targets[rowKey(w.records.collection, w.from)])
    .map((w) => ({
      collection: w.records.collection,
      from: w.from,
      to: targets[rowKey(w.records.collection, w.from)],
      count: w.count,
    }));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Records waiting for an audience</CardTitle>
        <CardDescription>
          Nobody can read these {appName} records: they were placed for audiences the app retired,
          or written before roles were on and never placed. An owner can show them to audiences;
          everyone who reads those audiences will then see them. Choose a place for every kind that
          points at another together.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {confirming && (
          <StepUpDialog
            open
            onOpenChange={(o) => !o && setConfirming(false)}
            title="Show unreadable records"
            description={`${chosen
              .map(
                (c) =>
                  `${c.count === 1 ? '1 record' : `${c.count} records`} of “${c.collection}” to “${audienceLabel(access.audiences, c.to)}”`,
              )
              .join(
                '; ',
              )}. Everyone who reads those audiences will be able to read them. The other owners are told.`}
            confirmLabel="Show them"
            onConfirm={async (creds) => {
              await redisclose.mutateAsync({
                moves: chosen.map(({ collection, from, to }) => ({ collection, from, to })),
                creds,
              });
            }}
            onSuccess={() => {
              setConfirming(false);
              setTargets({});
              toast({ title: 'Records shown to their audiences' });
            }}
          />
        )}
        {waiting.map(({ records: r, from, count }) => {
          const k = rowKey(r.collection, from);
          const options = access.audiences.filter(
            (a) => !a.deprecated && r.audiences.includes(a.id),
          );
          return (
            <div key={k} className="rounded-md border border-border p-3">
              <p>
                <span className="font-mono text-xs">{r.collection}</span> —{' '}
                {count === 1 ? '1 record' : `${count} records`}{' '}
                {from === null ? (
                  'in no audience (written before roles were on)'
                ) : (
                  <>
                    placed for “
                    <AppText appName={appName}>{audienceLabel(access.audiences, from)}</AppText>”
                  </>
                )}
              </p>
              {isOwner ? (
                <div className="mt-2">
                  <select
                    aria-label={
                      from === null
                        ? `Place ${r.collection} records in no audience in`
                        : `Show ${r.collection} records to`
                    }
                    className="h-9 rounded-md border border-input bg-background px-2"
                    value={targets[k] ?? ''}
                    onChange={(e) => setTargets((prev) => ({ ...prev, [k]: e.target.value }))}
                  >
                    <option value="">Leave them for now</option>
                    {options.map((a) => (
                      <option key={a.id} value={a.id}>
                        Show to {a.label}
                        {a.sensitive ? ' (sensitive)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="mt-1 text-muted-foreground">
                  An owner can show them to another audience.
                </p>
              )}
            </div>
          );
        })}
        {isOwner && (
          <Button
            variant="outline"
            disabled={chosen.length === 0 || redisclose.isPending}
            onClick={() => setConfirming(true)}
          >
            Show chosen records
          </Button>
        )}
        {redisclose.isError && appAccessErrorCode(redisclose.error) !== 'STEP_UP_REQUIRED' && (
          <p className="text-destructive">{describeAppAccessError(redisclose.error)}</p>
        )}
      </CardContent>
    </Card>
  );
}
